import { useCallback, useEffect, useState } from "react";

import {
    isServerReachable,
    isNativeApp,
    exitApp,
    currentApiBase
} from "../services/networkGuard";
import { clearStoredServer, setManualMode } from "../services/serverConfig";
import { discoverAndStoreTill } from "../services/discovery";

import "../styles/NetworkGate.css";

/**
 * Blocks the whole app until the restaurant till answers.
 *
 * It checks once at launch, and then keeps a light heartbeat while online: if
 * the till goes away (stopped, unplugged, off the network) it re-blocks after a
 * short grace period, so the waiter can't keep taking orders against a till that
 * isn't there. One missed beat is tolerated (a brief WiFi blip must not wall a
 * waiter mid-order); it re-blocks only after two consecutive misses.
 */
function NetworkGate({ children }) {

    // "checking" | "searching" | "online" | "offline"
    const [status, setStatus] = useState("checking");

    const runCheck = useCallback(async () => {

        setStatus("checking");

        // 1) Do we already know the till (remembered from a prior launch)?
        //    Try it a few times before giving up — a momentary WiFi blip must
        //    NOT trigger a full subnet scan when the till is actually still
        //    there and reachable (that flash of "searching" was the complaint).
        for (let attempt = 0; attempt < 4; attempt += 1) {
            // retry:1 → each attempt itself rides out one dropped packet, so a
            // weak signal gets several real chances on the KNOWN till before we
            // ever fall back to a full subnet scan.
            if (await isServerReachable(1)) {
                setStatus("online");
                return;
            }
            if (attempt < 3) await new Promise((r) => setTimeout(r, 1000));
        }

        // 2) Not known / moved (DHCP) / different network: auto-scan this WiFi.
        //    Finds the till by itself — the waiter never types an IP. On a
        //    different network nothing answers and we fall through to offline.
        setStatus("searching");
        const found = await discoverAndStoreTill();
        setStatus(found ? "online" : "offline");

    }, []);

    useEffect(() => {
        runCheck();
    }, [runCheck]);

    // While blocked (offline / stuck searching), keep probing the till on a timer
    // and RELOAD the moment it answers again. This is what auto-recovers the app:
    //  - when only the till's internet is toggled, the PHONE's network never
    //    changes, so the browser 'online' event never fires — a poll is the only
    //    signal the till is back;
    //  - after a WiFi drop+return the Android WebView's networking stays wedged,
    //    so an in-place re-probe hangs forever (why clearing from recents was the
    //    only fix). A reload resets the WebView's network stack, and the
    //    persistent cart is restored afterwards, so nothing is lost.
    useEffect(() => {

        if (status === "online") {
            return undefined;
        }

        let cancelled = false;
        let ticks = 0;
        let scanning = false;
        const reloadNow = () => { if (!cancelled) window.location.reload(); };

        const id = setInterval(async () => {
            ticks += 1;
            // Fast path: the remembered till answered again → reload (this also
            // resets a wedged WebView network stack after a WiFi drop+return).
            if (await isServerReachable() && !cancelled) { reloadNow(); return; }
            // Every ~15s, RE-SCAN the WiFi in case the till moved to a new IP.
            // This auto-recovers the "stuck searching" loop without the waiter
            // having to tap Try Again.
            if (!scanning && !cancelled && ticks % 3 === 0) {
                scanning = true;
                try { if (await discoverAndStoreTill() && !cancelled) reloadNow(); }
                finally { scanning = false; }
            }
        }, 5000);

        // When the waiter returns to the app, probe once right away (background
        // timers are throttled, so the poll may have been asleep) and reload if
        // the till is back. The phone's own 'online' event is a clean restart cue.
        const onVisible = async () => {
            if (document.visibilityState === "visible" && await isServerReachable() && !cancelled) reloadNow();
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("online", reloadNow);

        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("online", reloadNow);
        };

    }, [status]);

    // Heartbeat while online: if the till stops answering, re-block. Three
    // consecutive misses (a ~18s grace) before walling, so a momentary WiFi
    // hiccup doesn't kick a waiter out of an open cart. AND react at once when a
    // real request (e.g. sending an order) just failed to reach the till — that
    // is a concrete signal the till is gone, so confirm and block immediately
    // rather than making the waiter wait for the next heartbeat.
    useEffect(() => {
        if (status !== "online") {
            return undefined;
        }
        let misses = 0;
        let cancelled = false;

        const id = setInterval(async () => {
            // retry:1 so a single weak-signal blip on one beat isn't a "miss".
            const ok = await isServerReachable(1);
            if (cancelled) return;
            if (ok) { misses = 0; return; }
            misses += 1;
            // Three consecutive misses (~18s) before walling, so a flaky WiFi
            // doesn't keep bouncing the waiter to the "searching" screen.
            if (misses >= 3) setStatus("offline");
        }, 5000);

        const onUnreachable = async () => {
            // Confirm with a few probes so a single odd request (or a weak-signal
            // blip) doesn't wall the app; only block if the till really isn't
            // answering across retries.
            const ok = await isServerReachable(2);
            if (!cancelled && !ok) setStatus("offline");
        };
        window.addEventListener("inwallz:server-unreachable", onUnreachable);

        return () => {
            cancelled = true;
            clearInterval(id);
            window.removeEventListener("inwallz:server-unreachable", onUnreachable);
        };
    }, [status]);

    if (status === "online") {
        return children;
    }

    if (status === "checking" || status === "searching") {

        const message = status === "searching"
            ? "Searching for the restaurant server on this WiFi…"
            : "Connecting to restaurant server…";

        return (
            <div className="netgate-page">
                <div className="netgate-card">
                    <div className="netgate-spinner" />
                    <p className="netgate-checking">{message}</p>
                </div>
            </div>
        );

    }

    return (
        <div className="netgate-page">

            <div className="netgate-card">

                <div className="netgate-icon">📶</div>

                <h1>Can&apos;t reach the restaurant till</h1>

                <p className="netgate-body">
                    This app needs the restaurant till to be switched on and on the
                    same WiFi. Make sure the till (billing PC) is running, then try
                    again.
                </p>

                <div className="netgate-actions">

                    <button
                        type="button"
                        className="netgate-btn netgate-btn-primary"
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </button>

                    {isNativeApp() && (
                        <button
                            type="button"
                            className="netgate-btn netgate-btn-secondary"
                            onClick={exitApp}
                        >
                            Close App
                        </button>
                    )}

                </div>

                {/* Escape hatch for an unusual network the scan can't cover:
                    let a manager type the address instead of reinstalling. */}
                {isNativeApp() && (
                    <button
                        type="button"
                        className="netgate-link"
                        onClick={() => {
                            setManualMode(true);
                            clearStoredServer();
                            window.location.reload();
                        }}
                    >
                        Enter server manually
                    </button>
                )}

                {/* Shown so a manager can tell "wrong WiFi" from "wrong address"
                    without needing a developer on the phone. */}
                <p className="netgate-server">Server: {currentApiBase() || "(not set)"}</p>

            </div>

        </div>
    );

}

export default NetworkGate;
